import {ChangeDetectorRef, Component, OnDestroy, OnInit} from "@angular/core";
import {SessionModel} from "../../orchestration/session.model";
import {NavigationService} from "../../orchestration/navigation.service";
import {SessionController} from "../../orchestration/session.controller";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {Subscription} from "rxjs";
import {AnalyticsService, OrderPlacedEvent} from "../../orchestration/analytics.service";
import {ButtonProminence} from "../../buttons/button/button";
import {MessageLevel} from "../../notification/message/message";
import {OrderModel} from "../order.model";
import {OrderingContextModel} from "../../ordering-context/ordering-context.model";
import {OrderingPhase} from "../../orchestration/session-events";
import {Page} from "../../orchestration/page";
import {NavigationParams} from "../../orchestration/navigation-params";
import {CustomerDetails, CustomerDetailsService} from "../../customer-info/customer-details.service";
import {OrderPlacementStatusData} from "../order-placement-status.service";
import {RecentOrderData} from "../recent-order.service";
import {OrderPlacementStatus} from "../order-placement.service";
import {StoreData} from "../../store/store-search.model";
import {OrderType} from "../order";

@Component({
    selector: "woa-order-confirmation-page",
    templateUrl: "./order-confirmation-page.component.html",
    providers: [{provide: Page, useExisting: OrderConfirmationPageComponent}]
})

export class OrderConfirmationPageComponent extends Page implements OnInit, OnDestroy {
    private static readonly ORDER_ACCEPTANCE_POLL_INTERVAL: number = 5000;

    OrderType = OrderType;
    ButtonProminence = ButtonProminence;
    OrderPlacementStatus = OrderPlacementStatus;
    MessageLevel = MessageLevel;

    readonly progressSize: number = 120;
    readonly progressStrokeWidth: number = 20;
    readonly progressRadius: number = (this.progressSize / 2) - (this.progressStrokeWidth - 2);
    readonly progressCircumference: number = this.progressRadius * 2 * Math.PI;
    progressOffset: number = this.progressCircumference;
    progressSeen: boolean = false;

    recentOrder: RecentOrderData;
    pendingPhase: number = 1;
    localSecondsElapsed: number;
    timeoutSecondsPhase2: number;
    lastSecondsElapsed: number;
    lastTimeSync: number;
    timer: any;
    orderPlacedEvent: OrderPlacedEvent;

    private destroyed: boolean;
    private subscriptions: Subscription = new Subscription();

    constructor(navigationParams: NavigationParams,
                public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                private orderModel: OrderModel,
                private navigationService: NavigationService,
                private sessionController: SessionController,
                public responsiveService: ResponsiveService,
                private analyticsService: AnalyticsService,
                private changeDetectorRef: ChangeDetectorRef,
                private customerDetailsService: CustomerDetailsService) {
        super();
        this.orderPlacedEvent = navigationParams.get("orderPlacedEvent");

        // for display purposes only
        this.recentOrder = orderModel.buildRecentOrderData();
    }

    get hideBackButton(): boolean {
        return this.recentOrder.placedOrder.status === OrderPlacementStatus.PENDING || this.recentOrder.placedOrder.status === OrderPlacementStatus.ACCEPTED;
    }

    get firstName(): string {
        return this.recentOrder.orderInitiation.transferTargetId ? this.recentOrder.orderInitiation.firstName
            : this.recentOrder.orderPlacement ? this.recentOrder.orderPlacement.firstName
                : undefined;
    }

    get headingTitle(): string {
        return this.recentOrder.orderInitiation.transferTargetId ? (this.firstName ? "woa.heading.order.orderTransferred.withName" : "woa.heading.order.orderTransferred.anonymous")
            : this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.sendTableOrderReady ? (this.firstName ? "woa.heading.order.tableBistroOrderPlaced.withName.title" : "woa.heading.order.tableBistroOrderPlaced.anonymous.title")
                : this.recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? (this.firstName ? "woa.heading.order.tableOrderPlaced.withName.title" : "woa.heading.order.tableOrderPlaced.anonymous.title")
                    : (this.firstName ? "woa.message.order.orderPlaced.withName" : "woa.message.order.orderPlaced.anonymous");
    }

    get headingNotice(): string {
        return this.recentOrder.orderInitiation.tableNumber &&
        !this.recentOrder.orderInitiation.transferTargetId &&
        this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.sendTableOrderReady &&
        this.recentOrder.orderPlacement.mobilePhone &&
        this.sessionModel.isValidMobileNumberForSms(this.recentOrder.orderPlacement.mobilePhone) ? (
            this.firstName ? "woa.heading.order.tableBistroOrderPlaced.withName.notice" : "woa.heading.order.tableBistroOrderPlaced.anonymous.notice"
        ) : undefined;
    }

    async ngOnInit(): Promise<void> {
        this.subscriptions.add(this.responsiveService.mode$.subscribe(() => {
            this.changeDetectorRef.detectChanges();
        }));

        return this.awaitOrderAcceptance();
    }

    // noinspection JSUnusedGlobalSymbols
    onActive(): void {
        this.sessionModel.orderingPhase = OrderingPhase.COMPLETE;
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.subscriptions.unsubscribe();
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
    }

    async back(): Promise<any> {
        if (this.recentOrder.placedOrder.status === OrderPlacementStatus.ACCEPTED) {
            return this.sessionController.goToLandingPage();
        } else if (this.recentOrder.placedOrder.status !== OrderPlacementStatus.PENDING) { // CANCELLED or REJECTED
            // If this happens, it means the back or return buttons were available, which means there was an ordering failure, and we are allowing the user to go back to the ordering page.
            return this.navigationService.dismissPage(this);
        }
    }

    done(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.orderModel.completeOrder();
    }

    // noinspection JSUnusedGlobalSymbols
    getDirections(store: StoreData): void {
        // noinspection SpellCheckingInspection
        window.open("https://maps.apple.com/?daddr=" + store.latitude + "," + store.longitude, "_system", "noopener");
    }

    async awaitOrderAcceptance(): Promise<void> {
        this.timer = setInterval(() => {
            if (this.recentOrder.placedOrder.status === OrderPlacementStatus.PENDING && this.pendingPhase == 2 && this.localSecondsElapsed < this.timeoutSecondsPhase2) {
                this.localSecondsElapsed = this.lastSecondsElapsed + ((new Date().getTime() - this.lastTimeSync) / 1000);
                this.updateProgressOffset();
            }
        }, 1000);

        while (this.recentOrder.placedOrder.status === OrderPlacementStatus.PENDING) {
            const currentStatus: OrderPlacementStatus = this.recentOrder.placedOrder.status;
            await new Promise((resolve) => setTimeout(() => resolve(), OrderConfirmationPageComponent.ORDER_ACCEPTANCE_POLL_INTERVAL));
            if (this.destroyed) {
                return;
            }
            const orderPlacementStatus: OrderPlacementStatusData = await this.orderModel.getOrderPlacementStatus(this.orderModel.canPersistOrderDetails);
            this.timeoutSecondsPhase2 = orderPlacementStatus.timeoutSecondsPhase2;
            this.lastSecondsElapsed = Math.min(orderPlacementStatus.secondsElapsed, this.timeoutSecondsPhase2);
            this.lastTimeSync = new Date().getTime();
            if (orderPlacementStatus.status === OrderPlacementStatus.PENDING && this.lastSecondsElapsed >= orderPlacementStatus.timeoutSecondsPhase1) {
                this.pendingPhase = 2;
                if (this.localSecondsElapsed == undefined || this.lastSecondsElapsed > this.localSecondsElapsed) {
                    this.localSecondsElapsed = this.lastSecondsElapsed;
                }
                this.updateProgressOffset();
            }
            if (orderPlacementStatus.status !== currentStatus) {
                this.changeDetectorRef.detectChanges();

                if (this.recentOrder.placedOrder.status === OrderPlacementStatus.ACCEPTED) {
                    await this.analyticsService.trackOrderPlaced(this.orderPlacedEvent);
                } else if (this.recentOrder.placedOrder.status === OrderPlacementStatus.REJECTED) {
                    await this.analyticsService.trackPlaceOrderFailed("orderRejected");
                } else if (this.recentOrder.placedOrder.status === OrderPlacementStatus.CANCELLED) {
                    await this.analyticsService.trackPlaceOrderFailed("orderCancelled");
                }
            }
        }

        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }

        if (this.orderingContextModel.marketingOptIn && !this.sessionModel.waiterMode) {
            // noinspection ES6MissingAwait
            this.promptForMarketingDetails();
        }
    }

    async promptForMarketingDetails(): Promise<void> {
        let showMobilePhone: boolean = this.sessionModel.currentApp.collectMarketingMobilePhone && !this.recentOrder.orderPlacement.mobilePhone && !this.recentOrder.placedOrder.mobilePhoneKnown;
        let showEmail: boolean = this.sessionModel.currentApp.collectMarketingEmail && !this.recentOrder.orderPlacement.email && !this.recentOrder.placedOrder.emailKnown;
        let showPostCode: boolean = this.sessionModel.currentApp.collectMarketingPostCode && !this.recentOrder.orderPlacement.address.postCode && !this.recentOrder.placedOrder.postCodeKnown;
        if (!showMobilePhone && !showEmail && !showPostCode) {
            return;
        }

        const result: {
            confirmed: boolean,
            mobilePhone?: string,
            email?: string,
            postCode?: string
        } = await this.navigationService.createPage("woa-marketing-entry-page", {
            showMobilePhone: showMobilePhone,
            showEmail: showEmail,
            showPostCode: showPostCode
        });
        if (result && result.confirmed && (result.mobilePhone || result.email || result.postCode)) {
            if (result.mobilePhone || result.email) {
                let customerDetails: CustomerDetails = this.customerDetailsService.loadCustomer();
                if (result.mobilePhone) {
                    customerDetails.mobilePhone = result.mobilePhone;
                }
                if (result.email) {
                    customerDetails.email = result.email;
                }
                this.customerDetailsService.saveCustomer(customerDetails);
            }
            // noinspection ES6MissingAwait
            this.orderModel.performPostscript(result.email, result.mobilePhone, result.postCode, true);
        }
    }

    private updateProgressOffset(): void {
        setTimeout(() => {
            this.progressOffset = Math.max(this.progressCircumference - this.localSecondsElapsed / this.timeoutSecondsPhase2 * this.progressCircumference, 0);
            this.changeDetectorRef.detectChanges();
        }, this.progressSeen ? 0 : 500);
        this.progressSeen = true;
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order-confirmation-page/order-confirmation-page.component.ts